/**
 * The door.
 *
 * The only screen a signed-out player sees. It exists because everything behind it is
 * server-authoritative and needs a session cookie, and because the first thing ASHPORT should
 * say to somebody is what kind of place it is.
 *
 * No password strength meter, no confetti, no marketing copy. A name, a way back in, and a door.
 *
 * **PHASE 13 GAVE IT THE THREE THINGS IT NEEDED TO ACTUALLY OPEN.** A Turnstile widget, because
 * `server/src/routes/auth.ts` has demanded a token on every signup since Phase 1 and this screen
 * rendered none; a way back in for somebody who has forgotten their password, because
 * `/auth/reset/request` and `/auth/reset/confirm` had waited for a caller since the same phase;
 * and one sentence about what a testing key means, so a screenshot of a staging Gate cannot be
 * mistaken for a screenshot of a working defence.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { TURNSTILE_ACTION, type GatePayload } from '@ashport/shared'
import { A } from '../art'
import { api, ApiError } from '../net/api'
import { Art, Btn, Panel } from '../ui/Kit'
import { Icon } from '../ui/Icon'
import { Turnstile, type TurnstileHandle } from '../ui/Turnstile'
import { sz, useViewport } from '../ui/viewport'
import { useSession } from '../game/session'

type Mode = 'in' | 'up' | 'forgot'

const FIELD: React.CSSProperties = {
  width: '100%',
  background: 'var(--char)',
  border: '1px solid var(--line)',
  borderRadius: 2,
  color: 'var(--ink)',
  font: 'inherit',
  fontSize: 13,
  padding: '9px 10px',
  outline: 'none',
}

function Field({
  label,
  value,
  set,
  type = 'text',
  hint,
  auto,
  onEnter,
}: {
  label: string
  value: string
  set: (v: string) => void
  type?: string
  hint?: string
  auto?: string
  onEnter?: () => void
}) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span
        className="t-label"
        style={{ display: 'block', color: 'var(--ink-dim)', marginBottom: 5, letterSpacing: '.14em' }}
      >
        {label}
      </span>
      <input
        type={type}
        value={value}
        autoComplete={auto}
        onChange={(e) => set(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onEnter) onEnter()
        }}
        style={FIELD}
      />
      {hint && (
        <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-dim)', marginTop: 5 }}>
          {hint}
        </span>
      )}
    </label>
  )
}

/** A refusal, or an answer. Same plate, different colour, because both are the screen talking. */
export function Note({ tone, children }: { tone: 'bad' | 'good'; children: React.ReactNode }) {
  const c = tone === 'bad' ? 'var(--arrest)' : 'var(--clean)'
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        padding: '9px 10px',
        marginBottom: 12,
        border: `1px solid ${c}`,
        borderRadius: 2,
        background: `color-mix(in srgb, ${c} 12%, transparent)`,
        color: 'var(--ink)',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <Icon n={tone === 'bad' ? 'warn' : 'check'} s={13} c={c} />
      <span>{children}</span>
    </div>
  )
}

export function Gate() {
  const { signIn, register, fault, start } = useSession()
  const [mode, setMode] = useState<Mode>('in')
  const [handle, setHandle] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [answer, setAnswer] = useState<string | null>(null)

  /*
   * The sitekey, and the one read a signed-out browser can make.
   *
   * `null` while it is in flight, and a failure here is shown rather than swallowed: without a
   * sitekey the widget cannot render, without the widget there is no token, and without a token
   * signup is refused. A blank space where the challenge should be is the state that produced this
   * whole phase, so it says so instead.
   */
  const [gate, setGate] = useState<GatePayload | null>(null)
  const [gateFault, setGateFault] = useState<string | null>(null)

  /*
   * WHAT GOOGLE SENT BACK, read once from the query string. DESIGN-DELTAS 1t.2's ruling applied a
   * second time: this application has no router, the OAuth callback is a top-level navigation, and
   * a query parameter is the whole mechanism. The parameter is stripped with `replaceState` so a
   * reload is not a second attempt and a shared URL carries nobody's claim ticket.
   */
  const [google, setGoogle] = useState<{ result: string; ticket: string } | null>(() => {
    const q = new URLSearchParams(window.location.search)
    const result = q.get('google')
    if (result === null) return null
    const ticket = q.get('t') ?? ''
    window.history.replaceState({}, '', window.location.pathname)
    return { result, ticket }
  })
  const [handle2, setHandle2] = useState('')
  useEffect(() => {
    let dead = false
    void api
      .gate()
      .then((g) => {
        if (!dead) setGate(g)
      })
      .catch((e: unknown) => {
        if (!dead) setGateFault(e instanceof ApiError ? e.message : 'The door did not answer.')
      })
    return () => {
      dead = true
    }
  }, [])

  /*
   * A TURNSTILE TOKEN IS SINGLE USE AND THE WIDGET IS RESET AFTER EVERY ATTEMPT.
   *
   * Without the reset, a signup refused for a taken handle would fail its SECOND attempt on the
   * challenge instead, and the player would read a message about a security check when the real
   * problem was the name they typed. That is the commonest Turnstile integration bug and it is
   * invisible until somebody makes a mistake in the form.
   */
  const [token, setToken] = useState('')
  const widget = useRef<TurnstileHandle | null>(null)
  const burn = useCallback(() => {
    setToken('')
    widget.current?.refresh()
  }, [])

  // Switching between NEW NAME and a password reset drops whatever token was in hand. Both forms
  // now declare the SAME action, so a carried-over token would in fact be accepted, which is
  // precisely why it is dropped here instead: the button should read its own state honestly rather
  // than submit a challenge the player solved for a different question.
  useEffect(() => {
    setToken('')
    setProblem(null)
    setAnswer(null)
  }, [mode])

  const needsToken = mode !== 'in'
  const ready = !needsToken || token !== ''

  const submit = async () => {
    if (busy) return
    setBusy(true)
    setProblem(null)
    setAnswer(null)
    try {
      if (mode === 'in') {
        await signIn(email.trim(), password)
      } else if (mode === 'up') {
        await register({
          handle: handle.trim(),
          email: email.trim(),
          password,
          turnstile: token,
          tosVersion: gate?.tosVersion ?? 1,
        })
      } else {
        await api.resetRequest(email.trim(), token)
        /*
         * THE SAME SENTENCE WHETHER OR NOT THE ADDRESS EXISTS.
         *
         * The server takes deliberate care to answer identically in both cases, including in
         * timing. A client that said "we could not find that address" would hand back the
         * account-existence oracle the server just closed, in one line, on the same screen.
         */
        setAnswer('If that address has an account, a link is on its way. It is good for an hour.')
      }
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'That did not go through.')
    } finally {
      if (needsToken) burn()
      setBusy(false)
    }
  }

  const label =
    mode === 'in' ? 'GO IN' : mode === 'up' ? 'START' : 'SEND THE LINK'

  /**
   * The one thing Google cannot decide for a player: their name.
   *
   * `docs/PHASE-6B.md` 5.1 takes the handle at minute zero because "a name is required to exist",
   * and it is the name on the board for the life of the account. Deriving one from an email local
   * part would put `j.smith82` on a wanted list forever, so the callback creates nothing and this
   * is where the account is actually made.
   */
  const claim = async () => {
    if (busy || google === null) return
    setBusy(true)
    setProblem(null)
    try {
      await api.claimGoogle({ token: google.ticket, handle: handle2.trim() })
      await start()
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'That did not go through.')
    } finally {
      setBusy(false)
    }
  }

  /*
   * WHAT CAME BACK FROM GOOGLE, in words. Every one of these is a real outcome of the flow and the
   * refusal is the interesting one: `unverified` is the account-takeover rule firing, and it says
   * what to do rather than what went wrong.
   */
  const GOOGLE_SAID: Record<string, string> = {
    unavailable: 'Google sign in is not switched on here yet. Use an address instead.',
    expired: 'That took too long. Start again.',
    cancelled: 'Nothing happened. You can still use an address.',
    badstate: 'That did not come back the way it left. Start again.',
    unverified:
      'Google has not confirmed that address. Confirm it with Google, or open an account with a password here.',
    /*
     * THE LINK LANDING, and it has to say the password is gone.
     *
     * `server/src/routes/google.ts` destroys the account's password on every link, because the
     * ordinary case and an account pre-hijacking are indistinguishable from the server. Somebody
     * who genuinely had a password needs to be told, or they will try it forever and conclude the
     * game lost their account.
     */
    linked:
      'That address already had an account here and it is yours again. Any old password on it has been cleared: use Forgotten your password if you want one.',
  }
  const googleNote = google !== null ? GOOGLE_SAID[google.result] : undefined

  /*
   * THE DOOR AT PHONE WIDTH, since Phase 18. The form column was `flex: 0 0 400px` beside a hero
   * that took whatever was left, so on a 393px phone the hero was squeezed to nothing, its tagline
   * wrapped one word to a line down the left edge, and the form sat on top of it. Under 900px the
   * two stack: a band of the hero with the tagline on it, the form beneath, and the page scrolls.
   */
  const { width, touch } = useViewport()
  const narrow = width < 900

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: narrow ? 'column' : 'row',
        height: '100%',
        alignItems: 'stretch',
        background: 'var(--coal)',
        overflow: narrow ? 'auto' : 'hidden',
      }}
    >
      {/* The hero carries the tone. It is the first and last piece of art that gets a whole
          half of the screen, and it is why the form beside it can be this plain. */}
      <div style={narrow ? { flex: 'none', height: 250, position: 'relative' } : { flex: '1 1 0', minWidth: 0, position: 'relative' }}>
        <Art
          src={A('brand.login_hero')}
          alt="Ashport waterfront at night, seen from a rusted swing bridge"
          scrim="soft"
          style={{ position: 'absolute', inset: 0, height: '100%' }}
        />
        <div
          style={{
            position: 'absolute',
            left: narrow ? 18 : 40,
            bottom: narrow ? 16 : 40,
            right: narrow ? 18 : 40,
            zIndex: 3,
            textShadow: '0 2px 12px rgba(0,0,0,.8)',
          }}
        >
          <div
            className="t-display"
            style={{ fontSize: 46, letterSpacing: '.06em', color: 'var(--ink)', lineHeight: 1 }}
          >
            ASHPORT
          </div>
          <div style={{ color: 'var(--ink-mid)', fontSize: 14, marginTop: 10, maxWidth: 520 }}>
            A port city with a bad reputation and a worse memory. Twelve districts, twelve weeks,
            and a police department that only looks in one place at a time.
          </div>
        </div>
      </div>

      <div
        style={
          narrow
            ? { flex: 'none', display: 'flex', justifyContent: 'center', padding: 14, borderTop: '1px solid var(--line)' }
            : {
                flex: '0 0 400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 28,
                borderLeft: '1px solid var(--line)',
              }
        }
      >
        <Panel style={{ width: '100%', padding: 22 }}>
          {/*
            THE HANDLE STEP, and it replaces the whole form rather than sitting beside it.

            A player who has just come back from Google has one decision left and it is not which
            tab to be on. Showing the sign-in form underneath would offer them three ways to do
            something they have already done.
          */}
          {google?.result === 'claim' && google.ticket !== '' ? (
            <>
              <div
                className="t-display"
                style={{ fontSize: 22, letterSpacing: '.05em', color: 'var(--gold-hot)', lineHeight: 1 }}
              >
                PICK A NAME
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-mid)', lineHeight: 1.55, margin: '10px 0 14px' }}>
                Google confirmed who you are. This is the part it cannot do for you: the name here is
                the name on the board, and it does not change.
              </div>
              <Field
                label="What they call you"
                value={handle2}
                set={setHandle2}
                auto="username"
                onEnter={claim}
                hint="Three to sixteen characters."
              />
              {problem !== null && <Note tone="bad">{problem}</Note>}
              <Btn v="gold" full onClick={claim} disabled={busy || handle2.trim().length < 3}>
                {busy ? 'HOLD ON' : 'TAKE IT'}
              </Btn>
              <div style={{ marginTop: 12, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => setGoogle(null)}
                  style={{
                    background: 'none',
                    border: 0,
                    padding: 0,
                    font: 'inherit',
                    fontSize: 11.5,
                    color: 'var(--ink-dim)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Use an address instead
                </button>
              </div>
            </>
          ) : (
          <>
          <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
            {(['in', 'up'] as Mode[]).map((m) => (
              <button
                key={m}
                className="t-label"
                onClick={() => setMode(m)}
                style={{
                  flex: 1,
                  padding: '7px 0',
                  minHeight: touch ? 44 : undefined,
                  background: mode === m ? 'var(--panel-2)' : 'transparent',
                  border: '1px solid',
                  borderColor: mode === m ? 'var(--brass)' : 'var(--line)',
                  borderRadius: 2,
                  color: mode === m ? 'var(--brass-lit)' : 'var(--ink-dim)',
                  letterSpacing: '.14em',
                  cursor: 'pointer',
                }}
              >
                {m === 'in' ? 'SIGN IN' : 'NEW NAME'}
              </button>
            ))}
          </div>

          {mode === 'forgot' && (
            <div style={{ fontSize: 12, color: 'var(--ink-mid)', lineHeight: 1.55, marginBottom: 14 }}>
              Give us the address on the account. We send one link, it lasts an hour, and using it
              signs you out everywhere else.
            </div>
          )}

          {mode === 'up' && (
            <Field
              label="What they call you"
              value={handle}
              set={setHandle}
              auto="username"
              hint="Three to sixteen characters. This is the name on the board."
            />
          )}
          <Field
            label="Email"
            value={email}
            set={setEmail}
            type="email"
            auto="email"
            onEnter={mode === 'forgot' ? submit : undefined}
          />
          {mode !== 'forgot' && (
            <Field
              label="Password"
              value={password}
              set={setPassword}
              type="password"
              auto={mode === 'in' ? 'current-password' : 'new-password'}
              onEnter={submit}
              hint={
                mode === 'up'
                  ? 'Twelve characters or more. Nobody here can read it back to you.'
                  : undefined
              }
            />
          )}

          {needsToken && gate !== null && (
            <Turnstile
              sitekey={gate.turnstileSiteKey}
              action={TURNSTILE_ACTION}
              onToken={setToken}
              onFault={setProblem}
              handle={widget}
            />
          )}

          {gateFault !== null && <Note tone="bad">{gateFault}</Note>}
          {(problem ?? fault) && <Note tone="bad">{problem ?? fault}</Note>}
          {answer !== null && <Note tone="good">{answer}</Note>}

          <Btn v="gold" full onClick={submit} disabled={busy || !ready || gateFault !== null}>
            {busy ? 'HOLD ON' : ready ? label : 'ONE MOMENT'}
          </Btn>

          {/*
            SIGN IN WITH GOOGLE, and it is a top-level NAVIGATION rather than a fetch.

            Google will not be framed, and the `__Host-oauth` cookie that carries the state, the
            nonce and the PKCE verifier is `SameSite=Lax`, which withholds it from an XHR and sends
            it on exactly this: a top-level GET the user's own click caused. `window.location.assign`
            is the same call the Stripe checkout leg makes and for the same reason.
          */}
          {mode !== 'forgot' && (
            <>
              <button
                type="button"
                onClick={() => window.location.assign('/api/auth/google/start')}
                style={{
                  width: '100%',
                  marginTop: 12,
                  padding: '9px 0',
                  minHeight: touch ? 44 : undefined,
                  background: 'var(--panel-2)',
                  border: '1px solid var(--line)',
                  borderRadius: 2,
                  color: 'var(--ink)',
                  font: 'inherit',
                  fontSize: 12.5,
                  cursor: 'pointer',
                }}
              >
                Continue with Google
              </button>
              {googleNote !== undefined && (
                <div style={{ marginTop: 10 }}>
                  <Note tone={google?.result === 'linked' ? 'good' : 'bad'}>{googleNote}</Note>
                </div>
              )}
            </>
          )}

          {/*
            THE WAY BACK IN, and it was missing entirely.

            `POST /api/auth/reset/request` and `POST /api/auth/reset/confirm` have both existed and
            worked since Phase 1, with no method in `net/api.ts` and no control anywhere in the
            client that could reach either. A forgotten password was unrecoverable from the UI, for
            every account, permanently.
          */}
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setMode(mode === 'forgot' ? 'in' : 'forgot')}
              style={{
                background: 'none',
                border: 0,
                padding: touch ? '0 12px' : 0,
                minHeight: touch ? 44 : undefined,
                font: 'inherit',
                fontSize: 11.5,
                color: 'var(--ink-dim)',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {mode === 'forgot' ? 'Back to signing in' : 'Forgotten your password'}
            </button>
          </div>

          {/*
            THE PROMISE. It is a contract, and it is the first thing every player reads.

            The previous version said "No purchases, no boosts, no advertising", which is a
            promise that dies the day the game needs revenue. This one can be held forever, and
            it is held by `tools/content/validate.mjs`: the content gate walks every surface real
            money can reach and fails the build if any of them grants something that is not
            cosmetic. If you are editing this sentence, edit that check in the same change.
          */}
          <div style={{ marginTop: 14, fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
            Nothing you buy makes you stronger. No ads, no boosts, no shortcuts. Everything in
            Ashport is earned or taken.
          </div>

          {/*
            THE TERMS, WHERE THE PLAYER ACCEPTS THEM.

            `POST /api/auth/signup` has recorded `tos_version` against every account since Phase 1
            and `GET /api/auth/gate` has published the current version, so the server has always
            treated signup as acceptance of a document. The document was not written and the Gate
            did not link to anything, which means every account in this game accepted terms it had
            no way to read. That is worse than having no terms at all: it is a consent record with
            nothing behind it.

            Plain anchors, not router links: these are real pages served as static HTML from
            `game/public/`, outside the SPA, because Stripe's `consent_collection` needs a URL that
            resolves to the document itself and a crawler must be able to read one without running
            a bundle. `target="_blank"` so a half-filled signup form is not thrown away by reading
            what you are agreeing to.
          */}
          {mode === 'up' && (
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-dim)', lineHeight: 1.5 }}>
              Taking a name means you accept the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>
                Terms of Service
              </a>{' '}
              and the{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>
                Privacy Policy
              </a>
              . You must be 16 or over to play.
            </div>
          )}

          {/*
            SAID OUT LOUD, ON THE SCREEN, because the failure this phase opened with was not that
            the challenge was missing. It was that its absence was invisible: the door looked like
            it worked in every environment anybody had deployed and would have shut on the first
            real player. A build running Cloudflare's testing pair now says which one it is.
          */}
          {gate?.testing === true && (
            <div style={{ marginTop: 10, fontSize: sz(10.5), color: 'var(--heat)', lineHeight: 1.5 }}>
              This build runs the Turnstile testing keys. The widget is real and the round trip is
              real; the verdict is a constant, and no challenge is actually being scored.
            </div>
          )}
          </>
          )}
        </Panel>
      </div>
    </div>
  )
}
